import React, { useEffect, useRef, useState } from "react";
import { configSettingsApi } from "../../../providers/env";

type WhatsappQrWatcherProps = {
  intervalMs?: number;
  imgSize?: number;
  className?: string;
};

function binaryStringToBlob(binary: string, mime = "image/png"): Blob {
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: mime });
}

const WhatsappQrWatcher: React.FC<WhatsappQrWatcherProps> = ({
  intervalMs = 5000,
  imgSize = 256,
  className,
}) => {
  const [qrSrc, setQrSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastError, setLastError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const currentUrlRef = useRef<string | null>(null);

  const setObjectUrl = (blob: Blob | null) => {
    if (currentUrlRef.current) URL.revokeObjectURL(currentUrlRef.current);
    currentUrlRef.current = blob ? URL.createObjectURL(blob) : null;
    setQrSrc(currentUrlRef.current);
  };

  const fetchOnce = async () => {
    try {
      setLastError(null);
      const res: any = await (configSettingsApi as any).getWhatsappQr({
        responseType: "arraybuffer",
        params: { _: Date.now() },
        headers: { Accept: "image/png" },
      });

      const status = res?.status ?? res?.statusCode;
      const ct =
        res?.headers?.["content-type"] ||
        res?.headers?.get?.("content-type") ||
        "image/png";

      if (status === 200 && ct.includes("image/")) {
        const data = res?.data;
        if (data instanceof ArrayBuffer) {
          setObjectUrl(new Blob([data], { type: ct }));
        } else if (data instanceof Blob) {
          setObjectUrl(data);
        } else if (typeof data === "string") {
          setObjectUrl(binaryStringToBlob(data, ct));
        } else {
          setObjectUrl(null);
          setLastError("Unexpected data format from API");
        }
      } else if (status === 404) {
        setObjectUrl(null);
      } else {
        setObjectUrl(null);
        setLastError(`Request failed: ${status ?? "unknown"}`);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      setObjectUrl(null);
      setLastError(status === 404 ? null : err?.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnce();
    timerRef.current = window.setInterval(fetchOnce, intervalMs);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (currentUrlRef.current) URL.revokeObjectURL(currentUrlRef.current);
    };
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return (
    <div className={className} style={{ marginBottom: 16 }}>
      {loading ? (
        <div style={{ fontStyle: "italic", opacity: 0.7 }}>
          Checking WhatsApp status…
        </div>
      ) : qrSrc ? (
        <>
          <div style={{ color: "#0f9d58", fontWeight: 600, marginBottom: 8 }}>
            Please scan the QR to log in to the WhatsApp bot
          </div>
          <img
            src={qrSrc}
            alt="WhatsApp QR"
            width={imgSize}
            height={imgSize}
            style={{
              width: imgSize,
              height: imgSize,
              objectFit: "contain",
              border: "1px solid #eee",
              borderRadius: 12,
            }}
          />
        </>
      ) : (
        <div style={{ color: "#111", fontWeight: 600 }}>
          WhatsApp bot is already logged in
        </div>
      )}
      {lastError && (
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
          (Technical info: {lastError})
        </div>
      )}
    </div>
  );
};

export default WhatsappQrWatcher;
